import { AnimatePresence, motion } from "framer-motion";
import { Check, ShieldCheck, GitBranch, Sparkles } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { CHECKS } from "./workshopData";

const EASE = [0.16, 1, 0.3, 1];
const rise = (i = 0) => ({ initial: { opacity: 0, y: 18, z: -60, rotateX: 12 }, animate: { opacity: 1, y: 0, z: 0, rotateX: 0 }, transition: { duration: 0.75, ease: EASE, delay: 0.08 * i } });
const card = "rounded-2xl border border-white/[0.09] bg-white/[0.035] backdrop-blur-sm";
const kicker = "text-[10px] font-mono-lux tracking-[0.28em] uppercase text-[#D4AF37] mb-3";


const Understand = ({ ex }) => {
  const { t } = useLang();
  return (
    <div className="grid sm:grid-cols-2 gap-3">
      <motion.div {...rise(0)} className={`${card} p-5`}>
        <p className={kicker}>{t("ARI's understanding")}</p>
        <ul className="space-y-2">{ex.understand.map((u) => <li key={u} className="text-sm text-white/80 flex gap-2"><Check className="w-3.5 h-3.5 mt-0.5 text-[#E9CE7A] shrink-0" />{t(u)}</li>)}</ul>
      </motion.div>
      <motion.div {...rise(1)} className={`${card} p-5`}>
        <p className={kicker}>{t("ARI asks")}</p>
        <ul className="space-y-2">{ex.questions.map((q) => <li key={q} className="text-sm text-white/70 italic">“{t(q)}”</li>)}</ul>
      </motion.div>
    </div>
  );
};

const Design = ({ ex }) => {
  const { t } = useLang();
  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2.5">
        {ex.fields.map(([l, ty], i) => (
          <motion.div key={l} {...rise(i)} className={`${card} px-4 py-3`} data-testid={`workshop-field-${i}`}>
            <p className="text-sm text-[#F5EFE2]">{t(l)}</p>
            <p className="text-[10px] font-mono-lux tracking-[0.2em] uppercase text-[#D4AF37]/80 mt-1" data-no-translate>{ty}</p>
          </motion.div>
        ))}
      </div>
      <motion.div {...rise(ex.fields.length)} className={`${card} p-4 flex flex-wrap items-center gap-2 text-[12.5px] text-white/75`}>
        <GitBranch className="w-3.5 h-3.5 text-[#E9CE7A]" />
        {ex.workflow.map((w, i) => <span key={w} className="inline-flex items-center gap-2">{i > 0 && <span className="text-[#D4AF37]">→</span>}{t(w)}</span>)}
      </motion.div>
    </div>
  );
};

const Build = ({ ex }) => {
  const { t } = useLang();
  return (
    <div className="grid sm:grid-cols-5 gap-3">
      <motion.div {...rise(0)} className={`${card} p-5 sm:col-span-3`}>
        <p className={kicker}>{t(ex.title)}</p>
        <div className="space-y-2">{ex.fields.slice(0, 4).map(([l], i) => (
          <div key={l} className="flex items-center justify-between rounded-lg bg-black/25 px-3 py-2">
            <span className="text-[12.5px] text-white/60">{t(l)}</span><span className="h-1.5 rounded-full bg-[#D4AF37]/30" style={{ width: `${30 + i * 9}%` }} />
          </div>))}
        </div>
      </motion.div>
      <motion.div {...rise(1)} className={`${card} p-5 sm:col-span-2`}>
        <p className={kicker}>{t("Validation")}</p>
        <ul className="space-y-2.5">{CHECKS.map((c, i) => (
          <motion.li key={c} className="text-[12.5px] text-white/80 flex gap-2" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.5 + i * 0.45 }}>
            <Check className="w-3.5 h-3.5 mt-0.5 text-[#5FD3B0] shrink-0" />{t(c)}</motion.li>))}
        </ul>
      </motion.div>
    </div>
  );
};

const Publish = ({ ex }) => {
  const { t } = useLang();
  return (
    <motion.div {...rise()} className={`${card} p-6 flex flex-col sm:flex-row sm:items-center gap-5`} data-testid="workshop-publish-card">
      <div className="w-14 h-14 rounded-2xl flex items-center justify-center shrink-0" style={{ background: "linear-gradient(160deg,#F6E3A6,#C9A145)", boxShadow: "0 0 30px rgba(212,175,55,0.5)" }}>
        <ShieldCheck className="w-6 h-6 text-[#2A1F08]" />
      </div>
      <div>
        <p className="font-serif-lux text-2xl text-[#F5EFE2]">{t(ex.title)} <span className="text-[#5FD3B0] text-sm font-mono-lux tracking-[0.2em] uppercase ml-2">{t("Live")}</span></p>
        <p className="text-[12.5px] text-white/60 mt-1"><span data-no-translate>v1</span> · {t("Owner-authorised · audited · rollback ready")}</p>
      </div>
    </motion.div>
  );
};

const Describe = () => {
  const { t } = useLang();
  return (
    <motion.div {...rise()} className={`${card} p-5 flex gap-4 items-start`} data-testid="workshop-describe-card">
      <span className="mt-1 h-px w-6 shrink-0 bg-[#D4AF37]" />
      <p className="text-sm text-white/75 leading-relaxed">{t("Your request becomes the brief. ARI keeps it word for word through every stage, so nothing you asked for gets lost. Tap Next step to see what ARI does with it.")}</p>
    </motion.div>
  );
};

export const StageCanvas = ({ stage, ex, custom, prompt }) => {
  const { t } = useLang();
  const Comp = [Describe, Understand, Design, Build, Publish][stage];
  const body = <Comp ex={ex} />;
  return (
    <div style={{ perspective: 1200 }}>
      <div className={`${card} px-5 py-4 mb-4 flex items-center gap-3`} data-testid="workshop-request">
        <Sparkles className="w-4 h-4 text-[#E9CE7A] shrink-0" />
        <p className="text-[15px] text-[#F5EFE2] truncate">{prompt ? (custom ? prompt : t(prompt)) : t("Describe the tool you need…")}</p>
      </div>
      <AnimatePresence mode="wait">
        <motion.div key={`${stage}-${ex.id}-${custom}`} exit={{ opacity: 0, y: -10, transition: { duration: 0.25 } }} style={{ transformStyle: "preserve-3d" }}>{body}</motion.div>
      </AnimatePresence>
    </div>
  );
};

export default StageCanvas;
